import { Modal } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { FILTER_NAME_SIZE_LIMIT } from '../../../../../shared/savedFilters/common';
import titleCase from '../../../../../shared/utils/titleCase';
import useInputValue from '../../../../hooks/useInputValue';
import { onEnter } from '../../../../utils';
import Button from '../../Button/Button';
import ButtonGroup from '../../Button/ButtonGroup';
import { ErrorAlert } from '../../ErrorAlert';
import LabeledTextInput from '../../Forms/LabeledTextInput';
import type { AsyncStatus } from '../../../../types';
import type { InputElement } from '../../Forms/InputBase';
import type { ResourceType } from '../types';

type Props = {
  resourceType: ResourceType;
  currentName: string;
  isDisplayed: boolean;
  isSaving: boolean;
  errors?: AsyncStatus['errors'];
  onRename: (name: string) => void;
  onHide: () => void;
};

/**
 * RenameModal allows the user to rename the currentFilter state as a new filter.
 */
const RenameModal: VoidFunctionComponent<Props> = ({
  resourceType,
  currentName,
  isDisplayed,
  isSaving,
  errors,
  onHide,
  onRename,
}) => {
  const [name, setName] = useInputValue(currentName || '');
  const isSubmitDisabled = isSaving || currentName === name || name.trim().length < 1;
  //  Submit on keydown
  const onEnterKeyDown = onEnter<InputElement>(() => !isSubmitDisabled && onRename(name));
  return (
    <Modal
      animation
      show={isDisplayed}
      onHide={onHide}
      onEscapeKeyDown={onHide}
    >
      <Modal.Header>
        <Modal.Title>
          Rename {resourceType}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <LabeledTextInput
          labelVariant="legacy"
          label="Name"
          placeholder={`Name ${resourceType}`}
          value={name}
          onChange={setName}
          onKeyDown={onEnterKeyDown}
          maxLength={FILTER_NAME_SIZE_LIMIT}
          showCharCount
          autoFocus
          testId={`saved-${resourceType}-modal-input--rename`}
        />
        {errors && errors.length > 0 && (
          <div className="margin-top-md">
            <ErrorAlert status={{ errors }} />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type="secondary"
            size="medium"
            onClick={onHide}
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="medium"
            onClick={() => onRename(name)}
            isDisabled={isSubmitDisabled}
            testId={`saved-${resourceType}-modal-submit--rename`}
          >
            Rename {titleCase(resourceType)}
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default RenameModal;
